import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card.js';
import { Button } from '../../../components/ui/button.js';
import { Input } from '../../../components/ui/input.js';
import { Badge } from '../../../components/ui/badge.js';
import { Trash2, Plus, Settings, Users, Shield } from 'lucide-react';

interface WorkspaceConfig {
  id: string;
  name: string;
  displayName: string;
  status: 'active' | 'inactive' | 'pending';
  lpContact: string;
  permissions: string[];
  createdAt: string;
  lastAccessed?: string;
}

interface AdminWorkspacePanelProps {
  isAdmin: boolean;
}

export const AdminWorkspacePanel: React.FC<AdminWorkspacePanelProps> = ({ 
  isAdmin
}) => {
  const [workspaces, setWorkspaces] = useState<WorkspaceConfig[]>([]);
  const [newWorkspace, setNewWorkspace] = useState({
    name: '',
    displayName: '',
    lpContact: '',
    permissions: ['telemetry.read', 'metrics.export']
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAdmin) {
      fetchWorkspaces();
    }
  }, [isAdmin]);

  const fetchWorkspaces = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/workspaces', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch workspaces');
      }
      
      const data = await response.json();
      setWorkspaces(data.workspaces || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workspaces');
    } finally {
      setLoading(false);
    }
  };

  const createWorkspace = async () => {
    if (!newWorkspace.name || !newWorkspace.displayName || !newWorkspace.lpContact) {
      setError('All fields are required');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/admin/workspaces', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newWorkspace.name,
          displayName: newWorkspace.displayName,
          lpContact: newWorkspace.lpContact,
          permissions: newWorkspace.permissions
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create workspace');
      }

      const data = await response.json();
      setWorkspaces(prev => [...prev, data.workspace]);
      setNewWorkspace({
        name: '',
        displayName: '',
        lpContact: '',
        permissions: ['telemetry.read', 'metrics.export']
      });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create workspace');
    } finally {
      setLoading(false);
    }
  };

  const deleteWorkspace = async (workspaceId: string) => {
    if (!confirm('Are you sure you want to delete this workspace? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/admin/workspaces/${workspaceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete workspace');
      }

      setWorkspaces(prev => prev.filter(ws => ws.id !== workspaceId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete workspace');
    } finally {
      setLoading(false);
    }
  };

  const updateWorkspaceStatus = async (workspaceId: string, status: 'active' | 'inactive') => {
    try {
      setLoading(true);
      const response = await fetch(`/api/admin/workspaces/${workspaceId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (!response.ok) {
        throw new Error('Failed to update workspace status');
      }

      setWorkspaces(prev => prev.map(ws => 
        ws.id === workspaceId ? { ...ws, status } : ws
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update workspace status');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <Card className="bg-red-50 border-red-200">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 text-red-700">
            <Shield className="h-5 w-5" />
            <span>Admin access required for workspace management</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-blue-900">
            <Settings className="h-6 w-6" />
            LP Workspace Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Create New LP Workspace
              </h3>
              
              <div className="space-y-3">
                <div>
                  <label htmlFor="workspace-name" className="block text-sm font-medium text-gray-700 mb-1">Workspace ID</label>
                  <Input
                    id="workspace-name"
                    placeholder="LP_Alpha"
                    value={newWorkspace.name}
                    onChange={(e) => setNewWorkspace(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                
                <div>
                  <label htmlFor="display-name" className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                  <Input
                    id="display-name"
                    placeholder="Alpha Capital Partners"
                    value={newWorkspace.displayName}
                    onChange={(e) => setNewWorkspace(prev => ({ ...prev, displayName: e.target.value }))}
                  />
                </div>
                
                <div>
                  <label htmlFor="lp-contact" className="block text-sm font-medium text-gray-700 mb-1">LP Contact Email</label>
                  <Input
                    id="lp-contact"
                    type="email"
                    placeholder="contact@alphacapital.com"
                    value={newWorkspace.lpContact}
                    onChange={(e) => setNewWorkspace(prev => ({ ...prev, lpContact: e.target.value }))}
                  />
                </div>
                
                <Button 
                  onClick={createWorkspace} 
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? 'Creating...' : 'Create Workspace'}
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Users className="h-5 w-5" />
                Existing Workspaces ({workspaces.length})
              </h3>
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {workspaces.map((workspace) => (
                  <Card key={workspace.id} className="bg-white border-gray-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{workspace.displayName}</span>
                            <Badge 
                              variant={workspace.status === 'active' ? 'default' : 'secondary'}
                              className={workspace.status === 'active' ? 'bg-green-100 text-green-800' : ''}
                            >
                              {workspace.status}
                            </Badge>
                          </div>
                          <div className="text-sm text-gray-600">
                            ID: {workspace.name}
                          </div>
                          <div className="text-sm text-gray-600">
                            Contact: {workspace.lpContact}
                          </div>
                          <div className="text-xs text-gray-500">
                            Created: {new Date(workspace.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <select
                            value={workspace.status}
                            onChange={(e) => updateWorkspaceStatus(workspace.id, e.target.value as 'active' | 'inactive')}
                            className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteWorkspace(workspace.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                {workspaces.length === 0 && !loading && (
                  <div className="text-center py-8 text-gray-500">
                    No workspaces configured yet
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
